<template>
  <div class="last-import d-flex align-items-center gap-1">
    <span class="fw-bold gap-1">{{ $t('widgets.lastImport.label') }}</span>
    <span class="date">
      {{ $t('widgets.lastImport.on') }} {{ formattedDate }}
    </span>
  </div>
</template>

<script setup>
import { defineProps, computed, getCurrentInstance } from 'vue';

const props = defineProps({
  data: {
    type: Object,
    required: true,
    validator(value) {
      return value && value.last_import_date;
    },
  },
});

const { appContext } = getCurrentInstance();
const i18n = appContext.config.globalProperties.$i18n;

function tryParseDate(input) {
  if (!input && input !== 0) return null;

  if (typeof input === 'number') return new Date(input > 1e12 ? input : input * 1000);
  if (input instanceof Date) return input;

  if (typeof input === 'string') {
    let s = input.trim();

    const dmY = /^(\d{1,2})\.(\d{1,2})\.(\d{4})/;
    if (dmY.test(s)) {
      const [, d, m, y] = s.match(dmY);
      return new Date(`${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`);
    }

    const yMd = /^(\d{4}-\d{1,2}-\d{1,2})(?:[ T].*)?$/;
    if (yMd.test(s)) return new Date(s.replace(' ', 'T'));

    const dObj = new Date(s);
    if (!isNaN(dObj)) return dObj;
  }

  return null;
}

function ordinalSuffix(day) {
  const j = day % 10;
  const k = day % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

function parseDate(input, locale) {
  const date = tryParseDate(input);
  if (!date) return '-';

  if (locale.startsWith('en')) {
    const month = date.toLocaleString('en-US', { month: 'long' });
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}${ordinalSuffix(day)}, ${year}`;
  }

  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

const formattedDate = computed(() => {
  const locale = i18n?.locale || navigator.language || 'en-US';
  return parseDate(props.data?.last_import_date, locale);
});
</script>

<style scoped>
.date {
  font-weight: 500;
  color: #28a745;
}
</style>
